/* Sprint 2R-3B — Vognmand follow-up page (own drivers only). */
import React from "react";
import { scApi } from "../../lib/api";
import { Loader2, AlertOctagon, MessageSquare, Check } from "lucide-react";
import { toast } from "sonner";

const MSG = "EMA Kurer har registreret gentagne klager eller driftsudfordringer på en af jeres chauffører. Chaufføren skal booke en samtale med EMA Kurer. Se detaljer nedenfor.";

export default function SubcontractorFollowupPage() {
  const [cases, setCases] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [active, setActive] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await scApi().get("/sc/followup/cases");
      setCases(r.data.items || []);
    } catch {
      toast.error("Kunne ikke hente follow-up sager");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center gap-2 text-brand-text2 p-6"><Loader2 className="animate-spin" size={18}/> Indlæser...</div>;

  return (
    <div className="space-y-4 p-4 max-w-4xl mx-auto" data-testid="sc-followup-page">
      <div>
        <h1 className="font-display font-bold text-2xl text-brand-dark flex items-center gap-2">
          <AlertOctagon size={22} className="text-brand-red"/> Opfølgningssager
        </h1>
      </div>

      {cases.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
          {MSG}
        </div>
      )}

      {cases.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 p-6 text-center" data-testid="sc-followup-empty">
          <div className="text-emerald-700 font-semibold">Ingen aktive follow-up sager på dine chauffører 🎉</div>
        </div>
      ) : (
        <div className="bg-white border border-brand">
          <table className="w-full text-sm" data-testid="sc-followup-table">
            <thead>
              <tr className="bg-brand-bg2 border-b border-brand text-left text-xs uppercase text-brand-text2">
                <th className="p-3">Chauffør</th>
                <th className="p-3">Årsag</th>
                <th className="p-3">Severity</th>
                <th className="p-3">Værdi/tærskel</th>
                <th className="p-3">Status</th>
                <th className="p-3">Samtale</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <tr key={c.case_id} className="border-b border-gray-100" data-testid={`sc-case-${c.case_id}`}>
                  <td className="p-3 font-semibold">{c.driver_name}</td>
                  <td className="p-3 text-xs">{c.trigger_type}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase font-bold px-2 py-1 ${c.severity === "high_risk" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
                      {c.severity}
                    </span>
                  </td>
                  <td className="p-3 text-xs">{c.trigger_value} / {c.threshold_value}</td>
                  <td className="p-3 text-xs">{c.status}</td>
                  <td className="p-3 text-xs">{["conversation_required","high_risk","blocked_pending_review"].includes(c.severity) ? "Påkrævet" : "Nej"}</td>
                  <td className="p-3">
                    <button onClick={() => setActive(c)}
                            className="text-xs px-3 py-1 bg-white border border-brand hover:bg-brand-bg2"
                            data-testid={`sc-add-note-${c.case_id}`}>
                      <MessageSquare size={12} className="inline mr-1"/> Coaching note
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {active && <CoachingNoteModal caseDoc={active} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); }}/>}
    </div>
  );
}

function CoachingNoteModal({ caseDoc, onClose, onDone }) {
  const [note, setNote] = React.useState("");
  const [spoken, setSpoken] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (note.length < 4) return toast.error("Note skal være min. 4 tegn");
    setBusy(true);
    try {
      await scApi().post(`/sc/followup/cases/${caseDoc.case_id}/coaching-note`,
        { case_id: caseDoc.case_id, note,
          confirmed_spoken_with_driver: spoken });
      toast.success("Note gemt");
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (e?.message || "Fejl"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full max-w-md mx-4 border border-brand p-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-bold text-lg mb-3">Coaching note for {caseDoc.driver_name}</h3>
        <textarea value={note} onChange={(e) => setNote(e.target.value)}
                  rows={5} placeholder="Beskriv samtale med chauffør..."
                  className="w-full px-3 py-2 border border-brand text-sm mb-3"
                  data-testid="sc-coaching-note"/>
        <label className="flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" checked={spoken} onChange={(e) => setSpoken(e.target.checked)}
                  data-testid="sc-coaching-spoken"/>
          <Check size={14}/> Jeg har talt med chaufføren
        </label>
        <div className="flex gap-2">
          <button onClick={submit} disabled={busy}
                  className="flex-1 px-4 py-2 bg-brand-red hover:bg-red-700 text-white disabled:opacity-50"
                  data-testid="sc-coaching-submit">
            Gem
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-white border border-brand">Annuller</button>
        </div>
      </div>
    </div>
  );
}
